import React, { useState, useRef } from 'react';
import { X, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { users as usersApi } from '../../services/apiService';
import { Autocomplete } from '@anyemp/ui-kit';
import DateRangePicker from './DateRangePicker';
import { EVENT_TYPE_LABELS } from './EventCard';

const EVENT_TYPE_OPTIONS = Object.entries(EVENT_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

const HistoryFilters = ({ filters, onFiltersChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [managers, setManagers] = useState([]);
  const managersLoadedRef = useRef(false);

  const loadManagers = () => {
    if (managersLoadedRef.current) return;
    managersLoadedRef.current = true;
    usersApi.getAllUsers()
      .then(res => {
        const userList = res?.users || res?.data?.users || [];
        setManagers(userList);
      })
      .catch(err => {
        console.error('Error loading managers:', err);
        managersLoadedRef.current = false;
      });
  };

  const handleToggleExpand = () => {
    setIsExpanded(v => {
      const next = !v;
      if (next) loadManagers();
      return next;
    });
  };

  const handleChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFiltersChange({ eventTypes: [], userIds: [], dateFrom: '', dateTo: '' });
  };

  const activeCount =
    (filters.eventTypes?.length || 0) +
    (filters.userIds?.length || 0) +
    ((filters.dateFrom || filters.dateTo) ? 1 : 0);

  return (
    <div className="mb-4 border border-light-border-primary dark:border-dark-border-primary rounded-lg bg-light-bg-secondary dark:bg-dark-bg-secondary">
      <div
        className="flex items-center justify-between p-3 cursor-pointer select-none"
        onClick={handleToggleExpand}
      >
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
          <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
            Filters
          </span>
          {activeCount > 0 && (
            <span className="bg-primary-600 dark:bg-primary-500 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">
              {activeCount}
            </span>
          )}
        </div>
        {isExpanded
          ? <ChevronUp className="h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
          : <ChevronDown className="h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
        }
      </div>

      {isExpanded && (
        <div className="p-3 pt-0 space-y-3 border-t border-light-border-primary dark:border-dark-border-primary">
          <Autocomplete
            multiple
            clearable
            fullWidth
            label="Event Type"
            placeholder="All event types..."
            options={EVENT_TYPE_OPTIONS}
            value={filters.eventTypes || []}
            onChange={val => handleChange('eventTypes', val || [])}
          />

          <Autocomplete
            multiple
            clearable
            fullWidth
            label="Manager"
            placeholder="All managers..."
            options={managers.map(u => ({
              value: String(u.id),
              label: u.name || u.email || String(u.id)
            }))}
            value={filters.userIds || []}
            onChange={val => handleChange('userIds', val || [])}
          />

          <DateRangePicker
            label="Date Range"
            startDate={filters.dateFrom || ''}
            endDate={filters.dateTo || ''}
            onChange={({ startDate, endDate }) =>
              onFiltersChange({ ...filters, dateFrom: startDate || '', dateTo: endDate || '' })
            }
          />

          {activeCount > 0 && (
            <button
              onClick={clearFilters}
              className="flex items-center space-x-1 text-sm text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
            >
              <X className="h-4 w-4" />
              <span>Clear all filters</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryFilters;
